// Global Instructions Rule Applied!
//
// Deal form Execution tab — Round Investors card (owner-approved co-investor
// plan, 2026-08-13). Records WHO is in the round and for HOW MUCH (deal
// currency) — known early in execution, before the price per share fixes
// holdings. Our own funds are READ-ONLY rows derived from deal_commitments;
// co-investors are editable round_participations rows (linked account or
// free-text name, amount, Lead flag). Share counts stay on the cap table —
// its editor imports these rows as holders once the round prices. The footer
// reconciles ours + co-investors against the round's raise amount.
//
// Lead flags are stored uniformly (owner 2026-08-14): marking one of OUR
// rows as lead creates a round_participations 'account' row pointing at the
// fund's manager account (funds.managed_by, amount NULL) so
// `WHERE is_lead` lists every round's leads with no special case. Those
// us-manager rows are hidden from the co-investor list here — the derived
// ours rows carry their Lead state instead.
import React, { useMemo, useState } from 'react'
import { Button, Checkbox, Divider, Input, Popconfirm, Select, Table, Tag, Tooltip } from 'antd'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import InputNumber from '../../../core/components/InputNumber.jsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { useAccountOptions } from '../../asset_transactions/hooks/useAccountOptions.ts'
import AddCompanyModal from '../../companies/components/AddCompanyModal.tsx'
import {
  DealExecutionCommitmentRollupRow,
  DealExecutionParticipation
} from '../../../state_manager/data_models/deal_execution_board_model.ts'

const money = (value: number | null | undefined, symbol: string): string =>
  value == null
    ? '—'
    : `${symbol}${Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 })}`

interface RoundInvestorsCardProps {
  hasRound: boolean
  participations: DealExecutionParticipation[]
  rollup: DealExecutionCommitmentRollupRow[]
  /** Manager accounts of our managed_by_us funds — "us" for the Lead flag */
  usManagerAccountIds: string[]
  currencySymbol: string | null | undefined
  raiseAmount: number | null | undefined
  onSave: (participationId: string | null, fields: Record<string, unknown>) => Promise<boolean>
  onRemove: (participationId: string) => Promise<boolean>
}

interface InvestorRow {
  key: string
  kind: 'ours' | 'participation' | 'draft'
  participation?: DealExecutionParticipation
  fundName?: string | null
  // The fund's manager account — target of our lead participation row
  managedBy?: string | null
  amount?: number | null
  // Our amounts source from the commitment record until the subscription
  // books, then from the booked asset-transaction amounts (owner 2026-08-14).
  amountSource?: 'committed' | 'booked'
}

/** Booked asset-transaction amount once the subscription books, else the
 *  commitment record. */
export function effectiveOurAmount(r: DealExecutionCommitmentRollupRow): {
  amount: number | null
  source: 'committed' | 'booked'
} {
  if (r.booked != null && r.booked !== 0) return { amount: r.booked, source: 'booked' }
  return { amount: r.committed ?? null, source: 'committed' }
}

export default function RoundInvestorsCard({
  hasRound,
  participations,
  rollup,
  usManagerAccountIds,
  currencySymbol,
  raiseAmount,
  onSave,
  onRemove
}: RoundInvestorsCardProps): React.ReactElement {
  const { openFormInTab } = useTabManagerContext()
  const sym = currencySymbol || '$'
  // Options refresh whenever the dropdown opens, so accounts created
  // elsewhere (e.g. via the global add) appear without a hard reload.
  const { options: accountOptions, refetch: refetchAccountOptions, onOpenRefetch } =
    useAccountOptions()
  const [adding, setAdding] = useState(false)
  const [draftKind, setDraftKind] = useState<'account' | 'text'>('account')
  const [draftAccountId, setDraftAccountId] = useState<string | null>(null)
  const [draftName, setDraftName] = useState('')
  const [draftAmount, setDraftAmount] = useState<number | null>(null)
  const [draftIsLead, setDraftIsLead] = useState(false)
  const [addCompanyOpen, setAddCompanyOpen] = useState(false)

  // Inline create from the investor dropdown: refetch options, then select
  // the new account in the draft row.
  const handleCompanyCreated = async (accountId: string): Promise<void> => {
    setAddCompanyOpen(false)
    await refetchAccountOptions()
    setDraftKind('account')
    setDraftAccountId(accountId)
  }

  const resetDraft = (): void => {
    setAdding(false)
    setDraftKind('account')
    setDraftAccountId(null)
    setDraftName('')
    setDraftAmount(null)
    setDraftIsLead(false)
  }

  const saveDraft = async (): Promise<void> => {
    if (draftKind === 'account' && !draftAccountId) {
      showErrorToast('Pick the investor account (or switch to a named investor)')
      return
    }
    if (draftKind === 'text' && !draftName.trim()) {
      showErrorToast('Enter the investor name')
      return
    }
    const ok = await onSave(null, {
      participant_kind: draftKind,
      account_id: draftKind === 'account' ? draftAccountId : null,
      name: draftKind === 'text' ? draftName.trim() : null,
      amount: draftAmount,
      is_lead: draftIsLead
    })
    if (ok) resetDraft()
    else showErrorToast('Failed to add investor')
  }

  const patchRow = async (
    p: DealExecutionParticipation,
    updates: Record<string, unknown>
  ): Promise<void> => {
    const ok = await onSave(p.round_participations__id, {
      participant_kind: p.round_participations__participant_kind,
      account_id: p.accounts__id ?? null,
      name: p.round_participations__name ?? null,
      amount: p.round_participations__amount ?? null,
      is_lead: p.round_participations__is_lead ?? false,
      ...updates
    })
    if (!ok) showErrorToast('Failed to save investor change')
  }

  const usManagerIds = useMemo(() => new Set(usManagerAccountIds), [usManagerAccountIds])
  // Our lead rows: participations pointing at a us-manager account. Hidden
  // from the co-investor list; surfaced as the Lead state of the ours rows.
  const usRowByManager = useMemo(() => {
    const map = new Map<string, DealExecutionParticipation>()
    for (const p of participations) {
      if (p.accounts__id && usManagerIds.has(p.accounts__id)) map.set(p.accounts__id, p)
    }
    return map
  }, [participations, usManagerIds])

  const rows: InvestorRow[] = useMemo(() => {
    const ours: InvestorRow[] = rollup
      .filter(
        (r) => (r.committed != null && r.committed !== 0) || (r.booked != null && r.booked !== 0)
      )
      .map((r) => {
        const eff = effectiveOurAmount(r)
        return {
          key: `ours-${r.funds__id || 'all'}`,
          kind: 'ours' as const,
          fundName: r.funds__name,
          managedBy: r.funds__managed_by,
          amount: eff.amount,
          amountSource: eff.source
        }
      })
    const external: InvestorRow[] = participations
      .filter((p) => !(p.accounts__id && usManagerIds.has(p.accounts__id)))
      .map((p) => ({
        key: p.round_participations__id,
        kind: 'participation' as const,
        participation: p
      }))
    return [...ours, ...external, ...(adding ? [{ key: 'draft', kind: 'draft' as const }] : [])]
  }, [rollup, participations, usManagerIds, adding])

  const setOurLead = async (managedBy: string, checked: boolean): Promise<void> => {
    const usRow = usRowByManager.get(managedBy)
    if (checked) {
      if (usRow) await patchRow(usRow, { is_lead: true })
      else {
        const ok = await onSave(null, {
          participant_kind: 'account',
          account_id: managedBy,
          name: null,
          amount: null,
          is_lead: true
        })
        if (!ok) showErrorToast('Failed to mark us as lead')
      }
    } else if (usRow) {
      // A bare lead marker row carries nothing else — remove it entirely.
      if (usRow.round_participations__amount == null && !usRow.round_participations__notes) {
        const ok = await onRemove(usRow.round_participations__id)
        if (!ok) showErrorToast('Failed to clear lead flag')
      } else await patchRow(usRow, { is_lead: false })
    }
  }

  const oursTotal = rollup.reduce((n, r) => n + (effectiveOurAmount(r).amount || 0), 0)
  const coTotal = participations.reduce((n, p) => n + (p.round_participations__amount || 0), 0)
  const grandTotal = oursTotal + coTotal

  return (
    <FormSection
      title='Round Investors'
      titleColor='yellow'
      scrollable={false}
      className='h-full'
      headerActions={
        hasRound ? (
          <Button
            size='small'
            type='primary'
            icon={<PlusOutlined />}
            disabled={adding}
            onClick={() => setAdding(true)}
          >
            Investor
          </Button>
        ) : undefined
      }
    >
      {!hasRound ? (
        <div className='text-sm text-gray-400 py-2'>
          Link a funding round to record who is investing alongside us.
        </div>
      ) : (
        <div className='space-y-2'>
          <Table
            size='small'
            rowKey='key'
            pagination={false}
            dataSource={rows}
            columns={[
              {
                title: 'Investor',
                key: 'investor',
                render: (_, row) => {
                  if (row.kind === 'ours') {
                    const isLead =
                      !!row.managedBy && !!usRowByManager.get(row.managedBy)?.round_participations__is_lead
                    return (
                      <span>
                        {row.fundName || 'Unassigned'} <Tag className='ml-1'>Ours</Tag>
                        {isLead && (
                          <Tag color='blue' className='ml-1'>
                            Lead
                          </Tag>
                        )}
                      </span>
                    )
                  }
                  if (row.kind === 'draft') {
                    return (
                      <span className='flex items-center gap-2'>
                        <Select
                          size='small'
                          className='w-24 flex-none'
                          value={draftKind}
                          options={[
                            { value: 'account', label: 'Linked' },
                            { value: 'text', label: 'Named' }
                          ]}
                          onChange={(v) => setDraftKind(v as 'account' | 'text')}
                        />
                        {draftKind === 'account' ? (
                          <Select
                            size='small'
                            className='flex-1 min-w-0'
                            showSearch
                            optionFilterProp='label'
                            placeholder='Select account…'
                            value={draftAccountId ?? undefined}
                            options={accountOptions}
                            onDropdownVisibleChange={onOpenRefetch}
                            dropdownRender={(menu) => (
                              <>
                                {menu}
                                <Divider style={{ margin: '4px 0' }} />
                                <Button
                                  type='text'
                                  block
                                  size='small'
                                  icon={<PlusOutlined />}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => setAddCompanyOpen(true)}
                                >
                                  New account…
                                </Button>
                              </>
                            )}
                            onChange={(v) => setDraftAccountId(v ?? null)}
                          />
                        ) : (
                          <Input
                            size='small'
                            className='flex-1 min-w-0'
                            placeholder='Investor name'
                            value={draftName}
                            onChange={(e) => setDraftName(e.target.value)}
                          />
                        )}
                      </span>
                    )
                  }
                  const p = row.participation as DealExecutionParticipation
                  return (
                    <span>
                      {p.round_participations__participant_kind === 'account' && p.accounts__id ? (
                        <span
                          className='cursor-pointer text-blue-600 hover:underline'
                          onClick={() =>
                            openFormInTab(
                              'companies',
                              p.accounts__id as string,
                              p.accounts__name || 'Account'
                            )
                          }
                        >
                          {p.accounts__name || 'Account'}
                        </span>
                      ) : (
                        p.round_participations__name || '—'
                      )}
                      {p.round_participations__is_lead ? (
                        <Tag color='blue' className='ml-2'>
                          Lead
                        </Tag>
                      ) : null}
                    </span>
                  )
                }
              },
              {
                title: 'Amount',
                key: 'amount',
                width: 170,
                align: 'right' as const,
                render: (_, row) => {
                  if (row.kind === 'ours') {
                    return (
                      <span className='tabular-nums'>
                        {money(row.amount, sym)}
                        {row.amountSource === 'booked' && (
                          <Tag color='green' className='ml-1'>
                            booked
                          </Tag>
                        )}
                      </span>
                    )
                  }
                  if (row.kind === 'draft') {
                    return (
                      <InputNumber
                        size='small'
                        className='w-full'
                        isCurrency
                        currencySymbol={sym}
                        value={draftAmount ?? undefined}
                        onChange={(v: number | null) => setDraftAmount(v == null ? null : Number(v))}
                      />
                    )
                  }
                  const p = row.participation as DealExecutionParticipation
                  return (
                    <InputNumber
                      size='small'
                      className='w-full'
                      isCurrency
                      currencySymbol={sym}
                      value={p.round_participations__amount ?? undefined}
                      onBlur={(e: React.FocusEvent) => {
                        const raw = (e.target as HTMLInputElement).value.replace(/[^0-9.-]/g, '')
                        const value = raw === '' ? null : Number(raw)
                        if (value !== (p.round_participations__amount ?? null)) {
                          patchRow(p, { amount: value })
                        }
                      }}
                    />
                  )
                }
              },
              {
                title: 'Lead',
                key: 'lead',
                width: 60,
                align: 'center' as const,
                render: (_, row) => {
                  if (row.kind === 'ours') {
                    if (!row.managedBy) {
                      return (
                        <Tooltip title="Set the fund's manager (fund Terms tab) to record us as lead">
                          <Checkbox disabled />
                        </Tooltip>
                      )
                    }
                    return (
                      <Checkbox
                        checked={!!usRowByManager.get(row.managedBy)?.round_participations__is_lead}
                        onChange={(e) => setOurLead(row.managedBy as string, e.target.checked)}
                      />
                    )
                  }
                  if (row.kind === 'draft') {
                    return (
                      <Checkbox
                        checked={draftIsLead}
                        onChange={(e) => setDraftIsLead(e.target.checked)}
                      />
                    )
                  }
                  const p = row.participation as DealExecutionParticipation
                  return (
                    <Checkbox
                      checked={!!p.round_participations__is_lead}
                      onChange={(e) => patchRow(p, { is_lead: e.target.checked })}
                    />
                  )
                }
              },
              {
                title: '',
                key: 'actions',
                width: 70,
                align: 'right' as const,
                render: (_, row) => {
                  if (row.kind === 'ours') return null
                  if (row.kind === 'draft') {
                    return (
                      <span className='flex gap-1 justify-end'>
                        <Button size='small' type='primary' onClick={saveDraft}>
                          Add
                        </Button>
                        <Button size='small' type='text' onClick={resetDraft}>
                          ✕
                        </Button>
                      </span>
                    )
                  }
                  const p = row.participation as DealExecutionParticipation
                  return (
                    <Popconfirm
                      title='Remove this investor from the round?'
                      onConfirm={() => onRemove(p.round_participations__id)}
                    >
                      <Button size='small' type='text' danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  )
                }
              }
            ]}
          />
          <div className='text-xs text-gray-500 tabular-nums'>
            Ours {money(oursTotal, sym)} · Co-investors {money(coTotal, sym)} · Total{' '}
            {money(grandTotal, sym)}
            {raiseAmount ? (
              <>
                {' '}
                of {money(raiseAmount, sym)} raise (
                {((grandTotal / raiseAmount) * 100).toFixed(1)}%)
              </>
            ) : null}
          </div>
        </div>
      )}
      <AddCompanyModal
        open={addCompanyOpen}
        onCreated={handleCompanyCreated}
        onCancel={() => setAddCompanyOpen(false)}
      />
    </FormSection>
  )
}
